/**
 * BulkStudentImportDialog — polished CSV/Excel import on the SIS Students page.
 * Wires straight to /api/data-entry/bulk-import/student.
 * - Drag-drop + click-to-pick file zone
 * - Downloads a sample CSV template
 * - Inline guidance: required + optional fields + accepted values
 * - Granular row-level errors and per-row toast on success
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import {
  Upload, FileText, AlertCircle, CheckCircle2, XCircle, Download, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors backend FORM_SCHEMA["student"] and the field catalog
const STUDENT_FIELDS = [
  { name: 'name', required: true, hint: 'Full Name (e.g. "Sarah Nakato")' },
  { name: 'school', required: true, hint: 'suubi_primary | suubi_secondary' },
  { name: 'student_class', required: false, hint: 'e.g. P3, S2' },
  { name: 'gender', required: false, hint: 'male | female' },
  { name: 'date_of_birth', required: false, hint: 'YYYY-MM-DD' },
  { name: 'registration_number', required: false, hint: 'e.g. STU-2026-001' },
  { name: 'parent_names', required: false, hint: 'Full names of parent/guardian' },
  { name: 'parent_contact', required: false, hint: 'Phone (e.g. +256...)' },
  { name: 'home_address', required: false, hint: 'Village / parish / district' },
];

const SAMPLE_CSV = (() => {
  const header = STUDENT_FIELDS.map((f) => f.name).join(',');
  const rows = [
    'Sarah Nakato,suubi_primary,P3,female,2017-04-12,STU-2026-001,Ms. Mary Nakato,+256700111222,Mukono Town',
    'Daniel Okello,suubi_secondary,S2,male,2011-09-30,STU-2026-002,Mr. James Okello,+256700333444,Wakiso',
    'Grace Kirabo,suubi_primary,P5,female,2015-01-18,STU-2026-003,Ms. Jane Kirabo,+256700555666,Nakawa',
  ];
  return `${header}\n${rows.join('\n')}\n`;
})();

const BulkStudentImportDialog = ({ open, onClose, onImported }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const resetState = () => {
    setFile(null);
    setResult(null);
    setSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handlePickFile = (f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!(name.endsWith('.csv') || name.endsWith('.xlsx') || name.endsWith('.xls'))) {
      toast.error('Please choose a .csv or .xlsx file');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handlePickFile(e.dataTransfer.files?.[0]);
  }, []);

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_bulk_import_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const submit = async () => {
    if (!file) {
      toast.error('Choose a file first');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/data-entry/bulk-import/student`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(res.data);
      const ok = (res.data.approved || 0) + (res.data.pending || 0);
      const errs = res.data.errors?.length || 0;
      if (errs === 0) toast.success(`Imported ${ok} student${ok !== 1 ? 's' : ''}`);
      else toast.warning(`Imported ${ok} · ${errs} row${errs !== 1 ? 's' : ''} failed`);
      if (ok > 0) onImported?.();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Import failed';
      toast.error(typeof detail === 'string' ? detail : 'Import failed');
      setResult({ error: typeof detail === 'string' ? detail : 'Import failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl" data-testid="bulk-student-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Bulk Import Students
          </DialogTitle>
          <DialogDescription>
            Upload a <strong>CSV</strong> or <strong>Excel (.xlsx)</strong> file. First row must contain field names. Each row is one student.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Field reference */}
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileText className="h-4 w-4" /> Expected fields
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-download-template">
                <Download className="h-3.5 w-3.5 mr-1" /> Template
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {STUDENT_FIELDS.map((f) => (
                <div key={f.name} className="flex items-start gap-2">
                  <code className="font-mono text-[11px] bg-background border rounded px-1 py-px">
                    {f.name}
                  </code>
                  {f.required && (
                    <Badge variant="outline" className="h-4 px-1 text-[10px] bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300">
                      required
                    </Badge>
                  )}
                  <span className="text-muted-foreground">{f.hint}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Drop zone */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
            }`}
            data-testid="bulk-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => handlePickFile(e.target.files?.[0])}
              data-testid="bulk-file-input"
            />
            {file ? (
              <div className="space-y-1">
                <FileSpreadsheet className="h-10 w-10 mx-auto text-primary" />
                <div className="font-medium text-sm">{file.name}</div>
                <div className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB · Click or drop to replace
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                <div className="font-medium text-sm">Drop a file here, or click to choose</div>
                <div className="text-xs text-muted-foreground">.csv, .xlsx, .xls accepted</div>
              </div>
            )}
          </div>

          {/* Result panel */}
          {result && !result.error && (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                <div className="font-medium mb-1">Import complete</div>
                <div className="text-xs space-y-0.5">
                  <div><strong>Total rows:</strong> {result.total_rows}</div>
                  <div className="text-green-700 dark:text-green-300"><strong>Auto-approved:</strong> {result.approved}</div>
                  {result.pending > 0 && (
                    <div className="text-orange-700 dark:text-orange-300"><strong>Pending approval:</strong> {result.pending}</div>
                  )}
                  {result.errors?.length > 0 && (
                    <div className="text-red-700 dark:text-red-300 mt-2">
                      <strong>{result.errors.length} row{result.errors.length !== 1 ? 's' : ''} failed:</strong>
                      <ul className="ml-4 mt-1 list-disc max-h-32 overflow-y-auto">
                        {result.errors.slice(0, 10).map((er) => (
                          <li key={`bulk-err-${er.row}-${(er.error || '').slice(0, 20)}`} className="text-[11px]">Row {er.row}: {er.error}</li>
                        ))}
                        {result.errors.length > 10 && <li className="text-[11px]">…{result.errors.length - 10} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {result?.error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{result.error}</AlertDescription>
            </Alert>
          )}

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              The <code>school</code> column must be <code>suubi_primary</code> or <code>suubi_secondary</code>. The <code>gender</code> column accepts <code>male</code> or <code>female</code>. Dates use <code>YYYY-MM-DD</code> format.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="bulk-cancel-btn">Close</Button>
          <Button onClick={submit} disabled={!file || submitting} data-testid="bulk-submit-btn">
            {submitting ? 'Importing…' : <><Upload className="h-4 w-4 mr-2" /> Import students</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkStudentImportDialog;
